import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import InfiniteScrollSentinel from 'components/InfiniteScrollSentinel'
import { getAccessHistory } from 'services/fileService'
import { formatDateWithTime } from 'utils/date'

const WHAT_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'approved', label: 'Approved' },
  { key: 'refused', label: 'Refused' },
  { key: 'given', label: 'Given access' },
]

const GIVEN = ['self_granted', 'given_to_approver', 'legal_grant']

const HOW_FALLBACK = {
  approved: 'Through an access request.',
  refused: 'Through an access request.',
  self_granted: 'They gave themselves access to decide a request.',
  given_to_approver: 'Given access to approve requests.',
  legal_grant: 'Under a legal requirement.',
}

const rowKey = r => `${r.at}|${r.what}|${r.person?.id || ''}|${r.by?.id || ''}`

export const useAccessHistory = (fileId, enabled) => {
  const [state, setState] = useState({ rows: [], total: null, cursor: null, loading: true, loadingMore: false, forbidden: false, error: null })
  const fileRef = useRef(fileId)
  const cursorRef = useRef(null)
  const inFlight = useRef(false)
  const [reloadKey, setReloadKey] = useState(0)
  fileRef.current = fileId

  useEffect(() => {
    if (!enabled || !fileId) return undefined
    let cancelled = false
    inFlight.current = false
    cursorRef.current = null
    setState({ rows: [], total: null, cursor: null, loading: true, loadingMore: false, forbidden: false, error: null })
    getAccessHistory(fileId, null)
      .then(res => {
        if (cancelled) return
        const body = res?.data || {}
        cursorRef.current = body.meta?.nextCursor || null
        setState({ rows: body.data || [], total: body.meta?.total ?? null, cursor: cursorRef.current, loading: false, loadingMore: false, forbidden: false, error: null })
      })
      .catch(err => {
        if (cancelled) return
        const forbidden = [403, 404].includes(err?.response?.status)
        setState(s => ({ ...s, loading: false, forbidden, error: forbidden ? null : 'first' }))
      })
    return () => { cancelled = true }
  }, [fileId, enabled, reloadKey])

  const loadMore = useCallback(() => {
    const cursor = cursorRef.current
    if (!cursor || inFlight.current) return
    const id = fileRef.current
    inFlight.current = true
    setState(s => ({ ...s, loadingMore: true, error: null }))
    getAccessHistory(id, cursor)
      .then(res => {
        if (id !== fileRef.current) return
        const body = res?.data || {}
        cursorRef.current = body.meta?.nextCursor || null
        setState(cur => {
          const seen = new Set(cur.rows.map(rowKey))
          const incoming = (body.data || []).filter(r => !seen.has(rowKey(r)))
          return { ...cur, rows: [...cur.rows, ...incoming], total: body.meta?.total ?? cur.total, cursor: cursorRef.current, loadingMore: false }
        })
      })
      .catch(() => {
        if (id !== fileRef.current) return
        setState(cur => ({ ...cur, loadingMore: false, error: 'more' }))
      })
      .finally(() => { if (id === fileRef.current) inFlight.current = false })
  }, [])

  const retry = useCallback(() => setReloadKey(k => k + 1), [])

  return { ...state, hasMore: !!state.cursor && state.error !== 'more', loadMore, retry }
}

const sentence = r => {
  const person = r.person?.name || 'Someone'
  const by = r.by?.name || 'Someone'
  const date = formatDateWithTime(r.at)
  if (r.what === 'self_granted') return `${person} gave themselves access on ${date}`
  if (r.what === 'approved') return `${person} was approved by ${by} on ${date}`
  if (r.what === 'refused') return `${person} was refused by ${by} on ${date}`
  return `${person} was given access by ${by} on ${date}`
}

const AccessHistoryTab = ({ history }) => {
  const [q, setQ] = useState('')
  const [what, setWhat] = useState('all')
  const [dir, setDir] = useState('desc')
  const { rows, total, loading, loadingMore, hasMore, loadMore, error, retry } = history

  const filtering = q.trim() !== '' || what !== 'all'

  const visible = useMemo(() => {
    const term = q.trim().toLowerCase()
    const filtered = rows
      .filter(r => what === 'all' || (what === 'given' ? GIVEN.includes(r.what) : r.what === what))
      .filter(r => !term || `${r.person?.name || ''} ${r.by?.name || ''}`.toLowerCase().includes(term))
    return [...filtered].sort((a, b) => (new Date(a.at).getTime() - new Date(b.at).getTime()) * (dir === 'asc' ? 1 : -1))
  }, [rows, q, what, dir])

  if (loading) {
    return (
      <List aria-busy='true' aria-label='Loading the access history'>
        {[0, 1, 2, 3].map(i => (
          <Skeleton key={i} data-testid='history-skeleton'>
            <Bar $w='70%' />
            <Bar $w='45%' />
          </Skeleton>
        ))}
      </List>
    )
  }

  if (error === 'first') {
    return (
      <Wrap>
        <Empty>We couldn&apos;t load the access history. Please try again.</Empty>
        <RetryButton type='button' onClick={retry}>Try again</RetryButton>
      </Wrap>
    )
  }

  if (rows.length === 0) {
    return <Empty>No one has been given or refused access to this document through a request yet.</Empty>
  }

  return (
    <Wrap>
      <Intro>This shows approved requests, approver grants and legal grants for this document, and who decided each one.</Intro>
      <Controls>
        <Search aria-label='Search access history' placeholder='Search by name' value={q} onChange={e => setQ(e.target.value)} />
        <Chips role='group' aria-label='Filter by what happened'>
          {WHAT_FILTERS.map(f => (
            <Chip key={f.key} type='button' aria-pressed={what === f.key} $on={what === f.key} onClick={() => setWhat(f.key)}>{f.label}</Chip>
          ))}
        </Chips>
        <SortButton type='button' aria-label={`Sort by date, ${dir === 'desc' ? 'newest first' : 'oldest first'}`} onClick={() => setDir(d => (d === 'desc' ? 'asc' : 'desc'))}>
          Date {dir === 'desc' ? '▼' : '▲'}
        </SortButton>
      </Controls>
      <Count>
        {filtering
          ? `Showing ${visible.length} of ${rows.length} loaded so far`
          : total !== null ? `Showing ${rows.length} of ${total}` : `Showing ${rows.length}`}
      </Count>
      {visible.length === 0 ? (
        <Empty>Nothing matches. Try a different search or filter.</Empty>
      ) : (
        <List>
          {visible.map(r => (
            <Item key={rowKey(r)}>
              <Line>{sentence(r)}</Line>
              <Why>{r.how || HOW_FALLBACK[r.what]}</Why>
            </Item>
          ))}
        </List>
      )}
      {error === 'more' && (
        <Wrap>
          <Why>We couldn&apos;t load more of the history.</Why>
          <RetryButton type='button' onClick={loadMore}>Try again</RetryButton>
        </Wrap>
      )}
      <InfiniteScrollSentinel hasMore={hasMore} loading={loadingMore} onLoadMore={loadMore} />
    </Wrap>
  )
}

export default AccessHistoryTab

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const Intro = styled.p`
  margin: 0;
  font-size: 15px;
  line-height: 1.5;
  color: #4d4d56;
`

const Controls = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px 12px;
`

const Search = styled.input`
  flex: 1 1 220px;
  min-height: 44px;
  padding: 0 14px;
  border: 1px solid #e4e4e7;
  border-radius: 8px;
  font: inherit;
  font-size: 15px;
  max-width: 320px;

  &:focus { outline: 2px solid #33becc; outline-offset: 1px; }

  @media (max-width: 768px) {
    max-width: none;
    flex-basis: 100%;
  }
`

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`

const Chip = styled.button`
  min-height: 40px;
  padding: 0 14px;
  border-radius: 999px;
  border: 1px solid ${({ $on }) => ($on ? '#33becc' : '#e4e4e7')};
  background: ${({ $on }) => ($on ? '#fff' : '#fafafa')};
  color: ${({ $on }) => ($on ? '#0c1229' : '#4d4d56')};
  font: inherit;
  font-size: 15px;
  font-weight: ${({ $on }) => ($on ? 700 : 600)};
  cursor: pointer;
`

const SortButton = styled.button`
  min-height: 40px;
  padding: 0 12px;
  border: 0;
  background: transparent;
  font: inherit;
  font-size: 15px;
  font-weight: 700;
  color: #4d4d56;
  cursor: pointer;

  &:hover { color: #0c1229; }
`

const RetryButton = styled.button`
  align-self: flex-start;
  min-height: 44px;
  padding: 0 18px;
  border: 1px solid #33becc;
  border-radius: 8px;
  background: #33becc;
  color: #fff;
  font: inherit;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
`

const Count = styled.div`
  font-size: 15px;
  color: #4d4d56;
`

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const Item = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px 14px;
  border: 1px solid #e4e4e7;
  border-radius: 10px;
`

const Line = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: #0c1229;
  overflow-wrap: anywhere;
`

const Why = styled.div`
  font-size: 15px;
  color: #4d4d56;
`

const Empty = styled.p`
  margin: 0;
  padding: 16px 0;
  font-size: 15px;
  line-height: 1.5;
  color: #4d4d56;
`

const Skeleton = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 14px;
  border: 1px solid #e4e4e7;
  border-radius: 10px;
`

const Bar = styled.span`
  display: block;
  width: ${({ $w }) => $w};
  height: 12px;
  border-radius: 6px;
  background: #f1f5f9;
  animation: historyPulse 1.4s ease-in-out infinite;

  @keyframes historyPulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.55; }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`
